import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Form, Button, Table, Collapse } from 'react-bootstrap-v5';

export default function Matches() {
  const { id } = useParams(); 
  const navigate = useNavigate();

  const [matchData, setMatchData] = useState({
    coordinator_calls_date: '',
    coordinator_calls_outcome: '',
    coordinator_calls_match_with_client: '',
    coordinator_calls_restaurant: '',
    coordinator_calls_comments: '',
    coordinator_calls_reservation_no: '',
    coordinator_calls_date_sceduled_on: ''
  });

  const [matches, setMatches] = useState([]);
  const [status, setStatus] = useState('');
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [currentMatchId, setCurrentMatchId] = useState(id);
  const [isFormOpen, setIsFormOpen] = useState(true);

  const loadAllMatches = useCallback(async (headers) => {
    const url = `${process.env.REACT_APP_API_URL}/getallmatches/${id}`;//id is user_id
    
    try {
      const res = await axios.get(url, { headers });
      setMatches(res.data);
    } catch (err) {
      console.log("Error loading matches:", err);
    }
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    loadAllMatches(headers);
  }, [id, navigate, loadAllMatches, editId]); 

  useEffect(() => {
    if (matches.length === 0) {
      setIsFormOpen(true);
    } else {
      setIsFormOpen(false);
    }
  }, [matches.length]);

  const validateFields = () => {
    const requiredFields = {
      'Match Date': matchData.coordinator_calls_date,
      'Outcome': matchData.coordinator_calls_outcome,
      'Match With Client': matchData.coordinator_calls_match_with_client,
      'Restaurant': matchData.coordinator_calls_restaurant,
      'Comments': matchData.coordinator_calls_comments,
      'Reservation Number': matchData.coordinator_calls_reservation_no,
      'Date Scheduled': matchData.coordinator_calls_date_sceduled_on
    };

    const emptyFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([fieldName, _]) => fieldName);

    if (emptyFields.length > 0) {
      setError(`Please fill in all required fields: ${emptyFields.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/savematch/${currentMatchId}`;
    setStatus("saving...");
    setError("");

    try {
      const res = await axios.post(url, matchData, { headers });
      console.log(res.data);
      setStatus("saved");
      setError("");
      await loadAllMatches(headers);
      
      // Clear form and increment match ID for next entry
      setMatchData({
        coordinator_calls_date: '',
        coordinator_calls_outcome: '',
        coordinator_calls_match_with_client: '',
        coordinator_calls_restaurant: '',
        coordinator_calls_comments: '',
        coordinator_calls_reservation_no: '',
        coordinator_calls_date_sceduled_on: ''
      });
      const nextId = parseInt(currentMatchId) + 1;
      setCurrentMatchId(nextId.toString());
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.error || "error saving data");
      setStatus("");
    }
  };

  const handleUpdate = async (matchToEdit) => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const editUrl = `${process.env.REACT_APP_API_URL}/editmatch/${id}/${editId}`;//id is user_id
    try {
      await axios.put(editUrl, matchData, { headers });
      
      // Clear the form data after successful update
      setMatchData({
        coordinator_calls_date: '',
        coordinator_calls_outcome: '',
        coordinator_calls_match_with_client: '',
        coordinator_calls_restaurant: '',
        coordinator_calls_comments: '',
        coordinator_calls_reservation_no: '',
        coordinator_calls_date_sceduled_on: ''
      });
      
      // Reset the editId
      setEditId(null);
      
      // Refresh the matches table
      await loadAllMatches(headers);
    } catch (err) {
      console.log("Error editing match:", err);
      setError("Error editing match");
    }
  };

  const handleDelete = async (matchId) => {
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/deletematch/${matchId}`;

    try {
      await axios.delete(url, { headers });
      loadAllMatches(headers);
    } catch (err) {
      console.log("Error deleting match:", err);
    }
  };

  return (
    <div className="container">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-end mb-3">
            <Button 
              onClick={() => setIsFormOpen(!isFormOpen)}
              variant="outline-primary"
              size="sm"
            >
              {isFormOpen ? 'Hide Form' : 'Add Match'}
            </Button>
          </div>

          <Collapse in={isFormOpen}>
            <div>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Match Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={matchData.coordinator_calls_date}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_date: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Outcome</Form.Label>
                    <Form.Select 
                      value={matchData.coordinator_calls_outcome}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_outcome: e.target.value })
                      }
                      required
                    >
                      <option value="">Select an outcome</option>
                      <option value="accepted">Accepted profile</option>
                      <option value="Emailed">Emailed</option>
                      <option value="No answer">No Answer</option>
                      <option value="Rejected Call">Rejected call</option>
                      <option value="Rejected Profile">Rejected Profile</option>
                      <option value="Returned Call">Returned Call</option>
                      <option value="Talked to">Talked to</option>
                      <option value="Unreciprocated">Unreciprocated</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Match With Client</Form.Label>
                    <Form.Control
                      type="text"
                      value={matchData.coordinator_calls_match_with_client}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_match_with_client: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Restaurant</Form.Label>
                    <Form.Control
                      type="text"
                      value={matchData.coordinator_calls_restaurant}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_restaurant: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Date Scheduled</Form.Label>
                    <Form.Control
                      type="date"
                      value={matchData.coordinator_calls_date_sceduled_on}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_date_sceduled_on: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Reservation Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={matchData.coordinator_calls_reservation_no}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_reservation_no: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>Comments</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={matchData.coordinator_calls_comments}
                      onChange={(e) =>
                        setMatchData({ ...matchData, coordinator_calls_comments: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12">
                  {editId ? <Button onClick={handleUpdate} style={{ marginTop: "20px" }}>Update</Button> : 
                  <Button onClick={handleSave} style={{ marginTop: "20px" }}>Save</Button>}

                  <div className="status">{status}</div>
                  <div className="error" style={{ color: 'red' }}>{error}</div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <h2>All Matches</h2>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>Match With Client</th>
                <th>Outcome</th>
                <th>Comments</th>
                <th>Details</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((match) => (
                <tr key={match.id}>
                  <td>{match.coordinator_calls_date}</td>
                  <td>{match.coordinator_calls_match_with_client}</td>
                  <td>{match.coordinator_calls_outcome}</td>
                  <td>{match.coordinator_calls_comments}</td>
                  <td>
                    Date Scheduled: {match.coordinator_calls_date_sceduled_on}<br/>
                    Restaurant: {match.coordinator_calls_restaurant}<br/>
                    Reservation No: {match.coordinator_calls_reservation_no}
                  </td>
                  <td>
                    <Button variant="primary" size="sm" onClick={() => {
                      setEditId(match.id);
                      setIsFormOpen(true);
                      setMatchData({
                        coordinator_calls_date: match.coordinator_calls_date || '',
                        coordinator_calls_outcome: match.coordinator_calls_outcome || '',
                        coordinator_calls_match_with_client: match.coordinator_calls_match_with_client || '',
                        coordinator_calls_restaurant: match.coordinator_calls_restaurant || '',
                        coordinator_calls_comments: match.coordinator_calls_comments || '',
                        coordinator_calls_reservation_no: match.coordinator_calls_reservation_no || '',
                        coordinator_calls_date_sceduled_on: match.coordinator_calls_date_sceduled_on || ''
                      });
                    }} className="me-2">
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(match.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
